// Adapted from https://github.com/Azure-Samples/ms-identity-b2c-javascript-spa
/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 * For more details on using MSAL.js with Azure AD B2C, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/working-with-b2c.md
 */

import apiConfig from '@/auth/apiConfig'
import * as msal from '@azure/msal-browser'
import b2cPolicies from '@/auth/policies'

// msalConfig reads b2cPolicies (which resolves the Pinia useMsalStore), so it
// must be built lazily — on first access, after app.use(pinia) — to keep this
// module safe to static-import. Mirrors the lazy pattern in policies.js
// (events-app#912).
let cachedMsalConfig = null
function buildMsalConfig() {
    if (cachedMsalConfig) {
        return cachedMsalConfig
    }
    const authority = 'https://' + b2cPolicies.authorityDomain + '/pattanb2c.onmicrosoft.com/' + b2cPolicies.names.signUpSignIn
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
    cachedMsalConfig = {
        auth: {
            clientId: 'd6ecb4a5-e74d-4b20-b44d-fc88f6380fa4', // This is the ONLY mandatory field; everything else is optional.
            authority: authority, // Choose sign-up/sign-in user-flow as your default.
            knownAuthorities: [authority], // You must identify your tenant's domain as a known authority.
            // You must register this URI on Azure Portal/App Registration. Defaults to "window.location.href".
            redirectUri: process.env.VUE_APP_REDIRECT_URI
        },
        cache: {
            cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
            storeAuthStateInCookie: false, // If you wish to store cache items in cookies as well as browser cache, set this to "true".
        },
        system: {
            loggerOptions: {
                loggerCallback: (level, message, containsPii) => {
                    if (containsPii) {
                        return;
                    }
                    switch (level) {
                        case msal.LogLevel.Error:
                            console.error(message)
                            return
                        case msal.LogLevel.Info:
                            console.info(message)
                            return
                        case msal.LogLevel.Verbose:
                            console.debug(message)
                            return
                        case msal.LogLevel.Warning:
                            console.warn(message)
                            return
                    }
                }
            }
        }
    }
    return cachedMsalConfig
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
const loginRequest = {
    scopes: [...apiConfig.b2cScopes],
}

/**
 * Scopes you add here will be used to request a token from Azure AD B2C to be used for accessing a protected resource.
 * To learn more about how to work with scopes and resources, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
const tokenRequest = {
    scopes: [...apiConfig.b2cScopes],  // e.g. ["https://fabrikamb2c.onmicrosoft.com/helloapi/demo.read"]
    forceRefresh: false // Set this to "true" to skip a cached token and go to the server to get a new token
}

// msalConfig is exposed as a lazy getter so existing callers (authConfig.msalConfig)
// keep working unchanged while the build is deferred until after Pinia is active.
// loginRequest/tokenRequest only read apiConfig (no store), so they stay eager.
export default {
    get msalConfig() {
        return buildMsalConfig()
    },
    loginRequest,
    tokenRequest,
}
